/**
 * DEV-2521: classify errors from the `makeSalesforceRequest` callable.
 *
 * The proxy maps Salesforce HTTP statuses to Firebase codes (400 invalid-argument,
 * 403 permission-denied, 404 not-found, 409 already-exists, 500 internal) and places the
 * Salesforce envelope `{ status, success, error: { type, message } }` in `details`.
 *
 * `already-exists` must never be read as "duplicate account": the request context and the
 * returned `details.status` decide what happened.
 */

export type BookingErrorKind =
  | 'invalid' // 400: correctable input, keep the form values
  | 'forbidden' // 403: action unavailable for this caller
  | 'not-found' // 404: record missing, refresh
  | 'conflict' // 409: interval no longer available / cutoff / stage
  | 'server' // 500 with a Salesforce envelope: definite failure
  | 'indeterminate' // transport failure: the write may or may not have happened
  | 'unknown';

export interface ClassifiedBookingError {
  kind: BookingErrorKind;
  /** Salesforce status when the envelope was available. */
  status: number | null;
  /** Firebase functions code without the `functions/` prefix, when present. */
  code: string | null;
  /** Safe message for the owner. */
  message: string;
  /** True when the client should refresh bookings/availability before inviting a retry. */
  shouldRefresh: boolean;
  /** True when the owner may retry with the same inputs after any refresh. */
  retryable: boolean;
}

const DEFAULT_MESSAGES: Record<BookingErrorKind, string> = {
  invalid: 'Some of the booking details were not accepted. Please review and try again.',
  forbidden: 'This action is not available for your account.',
  'not-found': 'This booking could not be found. It may have been changed or removed.',
  conflict: 'The selected time is no longer available.',
  server: 'Something went wrong while saving. Please try again.',
  indeterminate:
    'We could not confirm whether the request was saved. Your bookings have been refreshed; please check before trying again.',
  unknown: 'Something went wrong. Please try again.'
};

const INDETERMINATE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'cancelled',
  'unknown',
  'aborted',
  'resource-exhausted',
  'network-error'
]);

function stripCode(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw === '') return null;
  return raw.startsWith('functions/') ? raw.slice('functions/'.length) : raw;
}

function readStatus(details: any): number | null {
  if (!details || typeof details !== 'object') return null;
  const candidate = details.status ?? details.error?.status ?? details.errorResponse?.status;
  if (typeof candidate === 'number' && Number.isFinite(candidate)) return candidate;
  if (typeof candidate === 'string' && /^\d{3}$/.test(candidate)) return Number(candidate);
  return null;
}

function readMessage(details: any): string | null {
  if (!details || typeof details !== 'object') return null;
  const message = details.error?.message ?? details.errorResponse?.error?.message ?? details.message;
  return typeof message === 'string' && message.trim() !== '' ? message : null;
}

function kindFromStatus(status: number): BookingErrorKind | null {
  switch (status) {
    case 400:
      return 'invalid';
    case 403:
      return 'forbidden';
    case 404:
      return 'not-found';
    case 409:
      return 'conflict';
    case 500:
      return 'server';
    default:
      return null;
  }
}

function kindFromCode(code: string | null, hasEnvelope: boolean): BookingErrorKind {
  switch (code) {
    case 'invalid-argument':
      return 'invalid';
    case 'permission-denied':
    case 'unauthenticated':
      return 'forbidden';
    case 'not-found':
      return 'not-found';
    case 'already-exists':
      return 'conflict';
    case 'internal':
      // A 500 with the Salesforce envelope is a definite failure; a bare `internal` from the
      // transport layer is not proof that nothing was written.
      return hasEnvelope ? 'server' : 'indeterminate';
    default:
      if (code && INDETERMINATE_CODES.has(code)) return 'indeterminate';
      return code ? 'unknown' : 'indeterminate';
  }
}

/** Classify any thrown/returned error from a booking, availability or cancel request. */
export function classifyBookingError(error: unknown): ClassifiedBookingError {
  const anyError: any = error ?? {};
  const code = stripCode(anyError.code);
  const details = anyError.details;
  const status = readStatus(details);
  const serverMessage = readMessage(details);
  const hasEnvelope = status !== null || serverMessage !== null;

  const kind = (status !== null && kindFromStatus(status)) || kindFromCode(code, hasEnvelope);

  const shouldRefresh =
    kind === 'conflict' || kind === 'not-found' || kind === 'forbidden' || kind === 'indeterminate';
  const retryable = kind === 'invalid' || kind === 'server' || kind === 'conflict' || kind === 'unknown';

  return {
    kind,
    status,
    code,
    message: serverMessage ?? DEFAULT_MESSAGES[kind],
    shouldRefresh,
    retryable
  };
}

/**
 * Diagnostic message for a failed availability request. Prefers the Salesforce envelope
 * message; when the `HttpsError` carries no envelope (for example a proxy that does not
 * allow the route yet), the Firebase message itself is surfaced so environment issues are
 * recognisable. Returns `null` when neither is available so the caller keeps its default.
 */
export function availabilityErrorMessage(error: unknown): string | null {
  const anyError: any = error ?? {};
  const details = anyError.details;
  const hasEnvelope = !!details && typeof details === 'object';
  if (hasEnvelope) {
    return readMessage(details);
  }
  const message = anyError.message;
  return typeof message === 'string' && message.trim() !== '' ? message.trim() : null;
}
